import { Box, Flex, Button, Text, Icon } from '@chakra-ui/core';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MdFileUpload } from 'react-icons/md';
import { LogoSlotConfig, ACCEPTED_LOGO_MIME_TYPES } from './CompanyLogoType';
import { convertToPng } from './convertLogoToPng';
import { validateLogoFile } from './FileUploadValidation';
import { LogoDeleteConfirmDialog } from './LogoDeleteConfirmDialog';
import { LogoPreview } from './LogoPreview';
import { UploadErrorAlert } from './UploadErrorAlert';

export interface LogoUploaderProps {
  config: LogoSlotConfig;
  currentLogoUrl?: string;
  isLoading: boolean;
  isSaving: boolean;
  error?: string;
  onUpload: (file: File) => Promise<void>;
  onDelete: () => Promise<void>;
  onDismissError: () => void;
}

const ACCEPT_ATTRIBUTE = ACCEPTED_LOGO_MIME_TYPES.join(',');

type PendingAction = 'upload' | 'delete' | null;

const LogoUploaderComponent: React.FC<LogoUploaderProps> = ({
  config,
  currentLogoUrl,
  isLoading,
  isSaving,
  error,
  onUpload,
  onDelete,
  onDismissError,
}) => {
  const { t } = useTranslation('uploadCompanyLogo');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [isValidatingFile, setIsValidatingFile] = useState(false);
  const [pendingAction, setPendingAction] = useState<PendingAction>(null);
  const [localValidationError, setLocalValidationError] = useState<string | undefined>(undefined);

  const isUploading = isSaving && pendingAction === 'upload';
  const isDeleting = isSaving && pendingAction === 'delete';
  const isBusy = isLoading || isValidatingFile || isSaving;

  useEffect(() => {
    if (error) {
      setLocalValidationError(undefined);
    }
  }, [error]);

  const displayedErrorMessage = localValidationError ?? error;

  const handleUploadClick = useCallback(() => {
    setLocalValidationError(undefined);
    if (error) {
      onDismissError();
    }
    fileInputRef.current?.click();
  }, [error, onDismissError]);

  const handleFileChange = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const selectedFile = event.target.files?.[0];
      // eslint-disable-next-line
      event.target.value = '';
      if (!selectedFile) {
        return;
      }

      setLocalValidationError(undefined);
      setIsValidatingFile(true);
      const validationResult = await validateLogoFile(selectedFile, config.shapeConstraint, t);
      setIsValidatingFile(false);

      if (!validationResult.isValid) {
        setLocalValidationError(validationResult.errors.join(' '));
        return;
      }

      setPendingAction('upload');
      try {
        const pngFile = await convertToPng(selectedFile);
        await onUpload(pngFile);
      } catch (/* eslint-disable-line */ error) {
        setLocalValidationError(t('logo.conversionError') ?? /* istanbul ignore next */ 'Failed to process image.');
      } finally {
        setPendingAction(null);
      }
    },
    [config.shapeConstraint, onUpload, t],
  );

  const handleDeleteTriggerClick = useCallback(() => {
    setLocalValidationError(undefined);
    if (error) {
      onDismissError();
    }
  }, [error, onDismissError]);

  const handleConfirmDelete = useCallback(async () => {
    setPendingAction('delete');
    try {
      await onDelete();
    } finally {
      setPendingAction(null);
    }
  }, [onDelete]);

  const handleDismissError = useCallback(() => {
    setLocalValidationError(undefined);
    /* istanbul ignore next */
    if (error) {
      onDismissError();
    }
  }, [error, onDismissError]);

  const skeletonTestId = useMemo(() => `logo-skeleton-${config.id}`, [config.id]);
  const errorTestId = useMemo(() => `logo-error-${config.id}`, [config.id]);
  const dismissAriaLabel = useMemo(() => `Dismiss ${config.title} error`, [config.title]);
  const uploadAriaLabel = useMemo(() => `Upload ${config.title}`, [config.title]);

  return (
    <Box borderTopWidth="1px" borderColor="gray.100" py={4}>
      <Flex justify="space-between" align="center" mb={2}>
        <Text fontSize="md" fontWeight="bold">
          {t(config.title)}
        </Text>
      </Flex>

      <Flex align="flex-start">
        <LogoPreview
          isLoading={isLoading}
          currentLogoUrl={currentLogoUrl}
          title={t(config.title)}
          isDeleting={isDeleting}
          testId={skeletonTestId}
        />

        <Box ml={6} maxWidth="50%">
          <Text color="gray.500" fontSize="sm">
            {t(config.description)}
          </Text>

          {displayedErrorMessage && (
            <UploadErrorAlert
              message={displayedErrorMessage}
              onDismiss={handleDismissError}
              ariaLabel={dismissAriaLabel}
              testId={errorTestId}
            />
          )}

          <Flex mt={4}>
            <Button
              rightIcon={<Icon as={MdFileUpload} boxSize="16px" />}
              variant="outline"
              onClick={handleUploadClick}
              isLoading={isValidatingFile || isUploading}
              loadingText={isValidatingFile ? t('uploadButton.validating') : t('uploadButton.uploading')}
              isDisabled={isBusy}
              mr={3}
            >
              {t('uploadButton.label')}
            </Button>
            <LogoDeleteConfirmDialog
              isBusy={isBusy}
              isDeleting={isDeleting}
              logoTitle={t(config.title)}
              currentLogoUrl={currentLogoUrl}
              onConfirmDelete={handleConfirmDelete}
              onTriggerClick={handleDeleteTriggerClick}
            />
          </Flex>

          <input
            ref={fileInputRef}
            type="file"
            accept={ACCEPT_ATTRIBUTE}
            onChange={handleFileChange}
            style={{ display: 'none' }}
            aria-label={uploadAriaLabel}
            disabled={isBusy}
          />
        </Box>
      </Flex>
    </Box>
  );
};

export const LogoUploader = React.memo(LogoUploaderComponent);
LogoUploader.displayName = 'LogoUploader';

export default LogoUploader;
